import translations from 'client/internationalization/translation-map.json';
import { HierarchyLevelType } from 'common/interfaces/hierarchy-entities';

export interface IFilesBrowserItem {
  id: string | number;
  parent: string | number;
  rawName: string;
  entityType: HierarchyLevelType;
  [key: string]: any;
}

export type FolderCrumb = { id: string; name: string };

const pluralize = (count: number, singular: string, plural: string) => {
  return count === 1 ? singular : plural;
};

export const formatCountText = (catalogueCount: number, fileCount: number, t: (key: string) => string) => {
  if (catalogueCount === 0 && fileCount === 0) return;
  const catalogueLabel = pluralize(catalogueCount, t(translations.catalogue), t(translations.catalogues)).toLowerCase();
  const fileLabel = pluralize(fileCount, t(translations.file), t(translations.files)).toLowerCase();
  return `${catalogueCount} ${catalogueLabel}  ·  ${fileCount} ${fileLabel}`;
};

export const toFolderPath = (folderIds: string[], items: IFilesBrowserItem[]) => {
  const itemsById = new Map(items.map((item) => [String(item.id), item]));
  const folderPath = [];
  for (const id of folderIds) {
    const folder = itemsById.get(id);
    if (!folder) break;
    folderPath.push({ id, name: folder.rawName });
  }
  return folderPath;
};

export const toBreadcrumbLinks = (
  folderPath: FolderCrumb[],
  rootLabel: string,
  goToRoot: () => void,
  goToCrumb: (index: number) => void,
) => {
  return [
    {
      label: rootLabel,
      active: folderPath.length === 0,
      ...(folderPath.length > 0 && { onClick: goToRoot }),
    },
    ...folderPath.map((crumb, index) => {
      const isLast = index === folderPath.length - 1;
      return {
        label: crumb.name,
        active: isLast,
        ...(!isLast && { onClick: () => goToCrumb(index) }),
      };
    }),
  ];
};

export const toTreeItems = (items: IFilesBrowserItem[], openFolder: (item: IFilesBrowserItem) => void) => {
  return items.map((item) => {
    const flattened = { ...item, parent: 0 };
    if (item.entityType !== HierarchyLevelType.Node) return flattened;
    return { ...flattened, onClick: () => openFolder(item) };
  });
};

export const isOSFileDrag = (event: React.DragEvent<HTMLDivElement>) => {
  return Array.from(event.dataTransfer?.types || []).includes('Files');
};

export const onOSFileDrag = (handler: (event: React.DragEvent<HTMLDivElement>) => void) => {
  return (event: React.DragEvent<HTMLDivElement>) => {
    if (!isOSFileDrag(event)) {
      return;
    }
    handler(event);
  };
};
